import React, { useCallback, useEffect, useState } from 'react';
import { createOrderRushRound, GameShell, usePausableTimeout } from '../shared';
import { formatGameCopy } from './gameCopy';

function OrderRushGame({ copy = {}, initialLevel = 1, isPaused = false, ...props }) {
    const startLevel = Math.max(1, Math.min(3, Math.round(Number(initialLevel) || 1)));
    const difficultyOffset = (startLevel - 1) * 4;
    const getDifficultyRound = (trialNumber) => difficultyOffset + trialNumber;
    const getLevel = (trialNumber) => Math.min(3, 1 + Math.floor((getDifficultyRound(trialNumber) - 1) / 4));
    const { clear: clearTimer, schedule: scheduleTimer } = usePausableTimeout(isPaused);
    const [round, setRound] = useState(1);
    const [trial, setTrial] = useState(() => createOrderRushRound(getDifficultyRound(1)));
    const [phase, setPhase] = useState('preview');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const needed = trial.order[currentIndex];
    const correctChoice = needed && choice === needed.id;

    const beginRound = useCallback((nextRound, nextStreak, excludeSetId = '') => {
        const nextTrial = createOrderRushRound(getDifficultyRound(nextRound), excludeSetId);
        setRound(nextRound);
        setTrial(nextTrial);
        setCurrentIndex(0);
        setChoice('');
        setPhase('preview');
        scheduleTimer(
            () => setPhase('input'),
            Math.max(1500, 2200 - nextStreak * 45)
        );
    }, [scheduleTimer]);

    useEffect(() => {
        scheduleTimer(() => setPhase('input'), 2200);
        return clearTimer;
    }, [clearTimer, scheduleTimer]);

    const choose = (item) => {
        if (isPaused || phase !== 'input' || !needed || choice) {
            return;
        }

        clearTimer();
        const correct = item.id === needed.id;
        setChoice(item.id);

        if (!correct) {
            setStreak(0);
            setPhase('feedback');
            scheduleTimer(() => {
                setChoice('');
                setPhase('input');
            }, 800);
            return;
        }

        const completed = currentIndex + 1 >= trial.order.length;
        if (completed) {
            const nextStreak = streak + 1;
            setScore((current) => current + 1);
            setStreak(nextStreak);
            setPhase('feedback');
            scheduleTimer(
                () => beginRound(round + 1, nextStreak, trial.assetSet.id),
                700
            );
            return;
        }

        setPhase('feedback');
        scheduleTimer(() => {
            setCurrentIndex((current) => current + 1);
            setChoice('');
            setPhase('input');
        }, 450);
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            disabled={isPaused}
            metrics={{ level: getLevel(round), score, streak }}
            tone={correctChoice ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className={`UFOV-brain-games__order-game is-${phase}`}>
                <div className="UFOV-brain-games__order-queue" aria-label={copy.orderQueue}>
                    {trial.order.map((item, index) => (
                        <div
                            className={`UFOV-brain-games__order-slot ${index < currentIndex ? 'is-done' : ''}`.trim()}
                            key={`${trial.id}-${item.id}`}
                        >
                            <span className="UFOV-brain-games__order-slot-inner">
                                <span className="UFOV-brain-games__order-face is-front">
                                    <img alt="" draggable="false" src={item.src} />
                                </span>
                                <span className="UFOV-brain-games__order-face is-back" />
                            </span>
                        </div>
                    ))}
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.orderRushCue}</div>
                <div className="UFOV-brain-games__order-tray">
                    {trial.tray.map((item, index) => {
                        const picked = choice === item.id;
                        const answer = choice && needed && item.id === needed.id;
                        return (
                            <button
                                aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                                className={`UFOV-brain-games__order-item ${picked ? 'is-picked' : ''} ${answer ? 'is-correct' : ''}`.trim()}
                                disabled={isPaused || phase !== 'input'}
                                key={`${trial.id}-tray-${item.id}`}
                                onClick={() => choose(item)}
                                type="button"
                            >
                                <img alt="" draggable="false" src={item.src} />
                            </button>
                        );
                    })}
                </div>
            </div>
        </GameShell>
    );
}

export default OrderRushGame;
